import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import HelpSidebar from '../components/help/HelpSidebar';
import HelpArticle from '../components/help/HelpArticle';
import HelpSearch from '../components/help/HelpSearch';
import HelpBreadcrumbs from '../components/help/HelpBreadcrumbs';
import helpConfig from '../content/help/help-config.json';
import { helpArticles } from '../content/help/help-articles';
import './Help.css';

const fallbackContent = '# Coming soon\n\nThis article is being drafted.';

const stripMarkdown = (value) =>
  value
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/[#>*_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getArticleIdFromPath = (pathname) => {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] !== 'help') return null;
  if (parts.length >= 3) return parts[2];
  return null;
};

const buildHelpData = () => {
  const categoriesById = {};
  helpConfig.categories.forEach((category) => {
    categoriesById[category.id] = category;
  });

  const articleById = {};
  const searchItems = helpConfig.articles.map((article) => {
    const content = helpArticles[article.id] || fallbackContent;
    const plainContent = stripMarkdown(content);
    const category = categoriesById[article.category];
    const path = `/help/${article.category}/${article.id}`;
    const mappedArticle = {
      ...article,
      content,
      categoryTitle: category?.title || 'Help Center',
      path,
    };
    articleById[article.id] = mappedArticle;
    return {
      id: article.id,
      title: article.title,
      content: plainContent,
      categoryId: article.category,
      categoryTitle: mappedArticle.categoryTitle,
      keywords: article.keywords || [],
      path,
    };
  });

  return { articleById, searchItems };
};

const { articleById, searchItems } = buildHelpData();

export default function Help() {
  const location = useLocation();
  const navigate = useNavigate();

  const defaultArticleId = helpConfig.categories[0]?.articles[0] || helpConfig.articles[0]?.id;
  const [selectedArticleId, setSelectedArticleId] = useState(defaultArticleId);
  const selectedArticle = articleById[selectedArticleId] || articleById[defaultArticleId];

  useEffect(() => {
    const articleIdFromPath = getArticleIdFromPath(location.pathname);
    if (articleIdFromPath && articleById[articleIdFromPath]) {
      setSelectedArticleId(articleIdFromPath);
      return;
    }

    if (articleIdFromPath && defaultArticleId) {
      const fallbackArticle = articleById[defaultArticleId];
      if (fallbackArticle) {
        navigate(fallbackArticle.path, { replace: true });
      }
      return;
    }

    if (!articleIdFromPath && defaultArticleId) {
      setSelectedArticleId(defaultArticleId);
    }
  }, [location.pathname, defaultArticleId, navigate]);

  useEffect(() => {
    if (!selectedArticle) return;
    if (location.pathname === '/help' || location.pathname === '/help/') {
      navigate(selectedArticle.path, { replace: true });
    }
  }, [location.pathname, navigate, selectedArticle]);

  const handleSelectArticle = (articleId) => {
    const article = articleById[articleId];
    if (!article) return;
    setSelectedArticleId(articleId);
    navigate(article.path);
  };

  return (
    <div className="help-page">
      <HelpSidebar
        categories={helpConfig.categories}
        articlesById={articleById}
        selectedArticleId={selectedArticle?.id}
        onSelectArticle={handleSelectArticle}
      />
      <section className="help-content" aria-live="polite">
        <HelpSearch
          items={searchItems}
          onSelectArticle={handleSelectArticle}
          selectedArticleId={selectedArticle?.id}
        />
        {selectedArticle ? (
          <>
            <HelpBreadcrumbs
              category={selectedArticle.categoryTitle}
              article={selectedArticle.title}
              onHomeClick={() => navigate('/help')}
            />
            <HelpArticle title={selectedArticle.title} content={selectedArticle.content} />
            <div className="help-support-footer">
              Need help? Contact support at <a href="mailto:support@policinglab.org">support@policinglab.org</a>
            </div>
          </>
        ) : (
          <div className="help-empty">Select an article to get started.</div>
        )}
      </section>
    </div>
  );
}
